import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api, downloadCsv, errorMessage } from "../api/client.ts";
import type { Deposit } from "../api/types.ts";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { DateRange, FilterBar, useUrlFilters } from "../components/Filters.tsx";
import { IconDownload } from "../components/Icons.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, AssetChip, Details, EmptyState, ExternalLink, Hash, PageHeader, Player, StatusPill, Time } from "../components/ui.tsx";
import { fmt, useT } from "../i18n/index.tsx";
import { useSession } from "../lib/session.tsx";
import { useAssets } from "../lib/assets.ts";
import { chainName, formatTimeFull, exactUnits, formatUnits, tokenOf } from "../lib/format.ts";
import { usePagedList } from "../lib/paging.ts";
import { Pager } from "../components/Pager.tsx";

const STATUSES = ["confirmed", "detected", "below_min", "flagged", "orphaned"];

export function Deposits() {
  const t = useT();
  const { values, set, setMany } = useUrlFilters(["q", "status", "asset", "from", "to"]);
  const { can } = useSession();
  const assets = useAssets();
  const list = usePagedList<Deposit>("/deposits", values);
  const [open, setOpen] = useState<Deposit | null>(null);
  const toast = useToast();
  // ?open=<id> (from the statement or the topbar lookup) opens that deposit's drawer.
  const [sp] = useSearchParams();
  const openId = sp.get("open");
  useEffect(() => {
    if (!openId) return;
    let alive = true;
    api.get<Deposit>(`/deposits/${encodeURIComponent(openId)}`).then((d) => alive && setOpen(d)).catch((e) => alive && toast(errorMessage(e), "error"));
    return () => {
      alive = false;
    };
  }, [openId, toast]);
  const closeDrawer = () => {
    setOpen(null);
    if (openId) set("open", "");
  };

  const columns: Column<Deposit>[] = [
    { key: "time", header: t.deposits.time, cell: (d) => <Time iso={d.detected_at} />, mobile: "title" },
    { key: "player", header: t.deposits.playerRef, cell: (d) => <Player refId={d.player_ref} name={d.player_name} /> },
    { key: "asset", header: t.common.asset, cell: (d) => <AssetChip asset={d.asset} chain={d.chain} compact /> },
    { key: "amount", header: t.deposits.amount, align: "right", cell: (d) => <strong className="amount" title={exactUnits(d.amount, d.decimals)}>{formatUnits(d.amount, d.decimals)}</strong> },
    { key: "fee", header: t.deposits.fee, align: "right", cell: (d) => <span className="amount muted">{d.fee === null ? "–" : formatUnits(d.fee, d.decimals)}</span>, mobile: "hide" },
    { key: "net", header: t.deposits.net, align: "right", cell: (d) => <span className="amount">{d.net === null ? "–" : formatUnits(d.net, d.decimals)}</span> },
    { key: "status", header: t.common.status, cell: (d) => <StatusPill status={d.status} /> },
    { key: "tx", header: t.deposits.txHash, cell: (d) => <Hash value={d.tx_hash} href={d.explorer_url} />, mobile: "hide" },
  ];

  const filtered = !!(values.q || values.status || values.asset || values.from || values.to);

  return (
    <div className="page">
      <PageHeader
        title={t.deposits.title}
        actions={
          can("deposits:export") && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => downloadCsv("/deposits/export.csv", values, `deposits-${new Date().toISOString().slice(0, 10)}.csv`).catch((e) => toast(errorMessage(e), "error"))}
            >
              <IconDownload size={18} /> {t.common.exportCsv}
            </button>
          )
        }
      >
        {t.deposits.intro}
      </PageHeader>

      <FilterBar
        q={values.q ?? ""} onQ={(v) => set("q", v)} searchLabel={t.deposits.searchLabel} hint={t.deposits.searchHint}
        status={values.status ?? ""} onStatus={(v) => set("status", v)} statuses={STATUSES}
        asset={values.asset ?? ""} onAsset={(v) => set("asset", v)} assets={assets}
      />
      <DateRange from={values.from ?? ""} to={values.to ?? ""} onChange={(from, to) => setMany({ from, to })} />

      {list.error ? (
        <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void list.reload()}>{t.common.retry}</button>}>{errorMessage(list.error)}</Alert>
      ) : (
        <>
          <DataTable
            caption={t.deposits.title}
            columns={columns}
            rows={list.items}
            rowKey={(d) => d.id}
            loading={list.loading}
            onRowClick={setOpen}
            rowLabel={(d) => fmt(t.deposits.rowLabel, { amount: formatUnits(d.amount, d.decimals), token: tokenOf(d.asset), player: d.player_name ? `${d.player_name} (${d.player_ref})` : d.player_ref, status: d.status })}
            footer={<Pager page={list.page} limit={list.limit} count={list.items.length} total={list.total} hasMore={list.hasMore} onPage={list.setPage} onLimit={list.setLimit} />}
            empty={
              <EmptyState title={filtered ? t.deposits.emptyFilteredTitle : t.deposits.emptyTitle} art={!filtered}>
                {filtered ? fmt(t.deposits.emptyFilteredBody, { hint: t.deposits.searchHint }) : t.deposits.emptyBody}
              </EmptyState>
            }
          />
        </>
      )}

      <DepositDrawer deposit={open} onClose={closeDrawer} />
    </div>
  );
}

function DepositDrawer({ deposit, onClose }: { deposit: Deposit | null; onClose: () => void }) {
  const t = useT();
  const [d, setD] = useState<Deposit | null>(deposit);
  useEffect(() => {
    setD(deposit);
    if (!deposit) return;
    let alive = true;
    api.get<Deposit>(`/deposits/${encodeURIComponent(deposit.id)}`).then((x) => alive && setD(x)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [deposit]);
  const token = d ? tokenOf(d.asset) : "";
  return (
    <Dialog open={!!deposit} onClose={onClose} kind="drawer" title={t.deposits.drawerTitle} description={d ? formatTimeFull(d.detected_at) : undefined}>
      {d && (
        <>
          <div className="drawer-hero">
            <div>
              <div className="drawer-amount" title={exactUnits(d.amount, d.decimals)}>{formatUnits(d.amount, d.decimals)} <span>{token}</span></div>
              <AssetChip asset={d.asset} chain={d.chain} />
            </div>
            <StatusPill status={d.status} />
          </div>
          {d.status === "flagged" && <Alert tone="warn">{d.flag_reason === "high_risk" ? t.deposits.flaggedRiskNote : t.deposits.flaggedNote}</Alert>}
          {d.status === "orphaned" && <Alert tone="error">{t.deposits.orphanedNote}</Alert>}
          {d.status === "below_min" && <Alert tone="info">{t.deposits.belowMinNote}</Alert>}
          <Details
            rows={[
              [t.deposits.amount, `${formatUnits(d.amount, d.decimals)} ${token}`],
              [t.deposits.fee, d.fee === null ? null : `${formatUnits(d.fee, d.decimals)} ${token}`],
              [t.deposits.netCredited, d.net === null ? null : <strong>{formatUnits(d.net, d.decimals)} {token}</strong>],
              [t.deposits.network, chainName(d.chain)],
              [t.deposits.playerRef, <Player refId={d.player_ref} name={d.player_name} copy />],
              [t.deposits.finality, d.finality],
              [t.deposits.depositAddress, <Hash value={d.address} full />, true],
              [t.common.from, <Hash value={d.from} full />, true],
              ...(d.risk?.labels.length ? [[t.deposits.riskReason, d.risk.labels.join(" · "), true] as [string, string, boolean]] : []),
              [t.deposits.txHash, <Hash value={d.tx_hash} full />, true],
              [t.deposits.block, <span className="mono-sm">{d.block_number}</span>],
              [t.deposits.logIndex, <span className="mono-sm">{d.log_index}</span>],
              [t.deposits.detected, <Time iso={d.detected_at} full />],
              [t.deposits.confirmed, d.confirmed_at ? <Time iso={d.confirmed_at} full /> : null],
              [t.deposits.depositId, <Hash value={d.id} full />, true],
            ]}
          />
          {d.explorer_url && (
            <div className="drawer-actions">
              <ExternalLink href={d.explorer_url}>{t.deposits.viewOnExplorer}</ExternalLink>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}
